import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { ConnectionType, IConnectionDetailsDTO, IConnectionGroupDTO, IMeetingConnectionRequestParams, KnovioLabel } from '../../../../../../api/connections.dao';
import { IMeetingLinkDTO, IMeetingLinksDAO, MeetingLinkRole } from '../../../../../../api/meeting-links.dao';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { isTrackingFieldEnabled, TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';
import { getConnectionItemLabelText, isConnectionGroupType } from '../../../../../../infrastructure/utils/connections.utils';
import { handleApiException, showNotification } from '../../../../../global/modal/alerts-host/alert-templates';
import { NOT_FOUND_STATUS } from '../../../../../../constants/common.constants';
import {
    GROUP_CONNECTION_TYPE,
    MEETING_LINK_ADDED_TEXT,
    MEETING_LINK_UPDATED_TEXT,
    NONE_CONNECTION_TYPE,
} from './meeting-links.texts';
import { MeetingLinkInheritanceIds } from './manage-meeting-link-modal/manage-meeting-link-modal.viewmodel';

const FORBIDDEN_STATUS = 403;

class MeetingLinksViewModel {
    @mobx.observable
    private meetingLinks: IMeetingLinkDTO[] = [];

    constructor(
        private readonly meetingApiParams: IMeetingConnectionRequestParams,
        @IoC.Inject private readonly meetingLinksDAO: IMeetingLinksDAO = IoC.Container.get(IMeetingLinksDAO),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage)
    ) { }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get links(): IMeetingLinkDTO[] {
        return this.meetingLinks;
    }

    public loadLinks = async (): Promise<void> => {
        try {
            const links = await this.meetingLinksDAO.getMeetingLinks(this.meetingApiParams);
            mobx.runInAction(() => {
                this.meetingLinks = links;
            });
        } catch (error) {
            const status = error?.response?.status;
            if (status === NOT_FOUND_STATUS || status === FORBIDDEN_STATUS) {
                mobx.runInAction(() => {
                    this.meetingLinks = [];
                });
                return;
            }
            handleApiException(error);
        }
    };

    @mobx.action
    public onLinkCreated = (link: IMeetingLinkDTO, connectionType: string): void => {
        this.meetingLinks = [...this.meetingLinks, link];
        this.trackMeetingLinkEvent(TRACKING_EVENTS.ADD_MEETING_LINK, link, connectionType);
        showNotification(this.intl.formatMessage(MEETING_LINK_ADDED_TEXT));
    };

    @mobx.action
    public onLinkUpdated = (link: IMeetingLinkDTO, connectionType: string): void => {
        this.meetingLinks = this.meetingLinks.map(existing =>
            existing.meetingLinkId === link.meetingLinkId ? link : existing
        );
        this.trackMeetingLinkEvent(TRACKING_EVENTS.EDIT_MEETING_LINK, link, connectionType);
        showNotification(this.intl.formatMessage(MEETING_LINK_UPDATED_TEXT));
    };

    public trackCopyMeetingLink = (): void => {
        this.trackingService.get().track(TRACKING_EVENTS.COPY_MEETING_LINK, this.defaultTrackingProps());
    };

    public getConnectionDisplayName = (
        link: IMeetingLinkDTO,
        connections: IConnectionDetailsDTO[],
        connectionGroups: IConnectionGroupDTO[],
        inheritanceConnections: (IConnectionDetailsDTO | IConnectionGroupDTO)[] = [],
        inheritanceIds?: MeetingLinkInheritanceIds
    ): string => {
        // Inherited links follow the live form (draft). Apply persists; cancel restores the previous form state.
        if (link.connectionInherited && link.role && inheritanceIds) {
            const resolvedId = this.resolveInheritedConnectionId(link.role, inheritanceIds);
            const resolved = this.findEntityById(
                resolvedId,
                connections,
                connectionGroups,
                inheritanceConnections
            );
            return resolved ? getConnectionItemLabelText(resolved) : '';
        }
        const assigned = this.findAssignedEntity(link, connections, connectionGroups, inheritanceConnections);
        if (assigned) {
            return getConnectionItemLabelText(assigned);
        }
        return '';
    };

    public getRoleLabel = (role: MeetingLinkRole | null): string => {
        return role ?? '';
    };

    public static getConnectionTypeForTracking(
        connectionId: number | null,
        connections: IConnectionDetailsDTO[],
        connectionGroups: IConnectionGroupDTO[]
    ): string {
        if (connectionId == null) {
            return NONE_CONNECTION_TYPE;
        }
        const group = connectionGroups.find(item => item.meetingConnectionId === connectionId);
        if (group || connections.some(item => item.meetingConnectionId === connectionId && isConnectionGroupType(item))) {
            return GROUP_CONNECTION_TYPE;
        }
        const connection = connections.find(item => item.meetingConnectionId === connectionId);
        if (!connection) {
            return NONE_CONNECTION_TYPE;
        }
        return connection.type === ConnectionType.Knovio ? KnovioLabel.OEStream : connection.type;
    }

    private trackMeetingLinkEvent = (
        eventName: TRACKING_EVENTS,
        link: IMeetingLinkDTO,
        connectionType: string
    ): void => {
        this.trackingService.get().track(eventName, {
            ...this.defaultTrackingProps(),
            'Connection Type': connectionType,
            Role: link.role ?? NONE_CONNECTION_TYPE,
            'Panelist link creation': isTrackingFieldEnabled(!!link.createPanelistLinkEnabled),
        });
    };

    private defaultTrackingProps = (): Record<string, string | number> => {
        return {
            'Account ID': this.globalStorage.getAccount().accountId,
            'Account Name': this.globalStorage.getAccount().name,
            'Event ID': this.globalStorage.getEvent().eventId,
            'Event Name': this.globalStorage.getEvent().name,
            'Meeting ID': this.meetingApiParams.meetingId,
        };
    };

    private findAssignedEntity(
        link: IMeetingLinkDTO,
        connections: IConnectionDetailsDTO[],
        connectionGroups: IConnectionGroupDTO[],
        inheritanceConnections: (IConnectionDetailsDTO | IConnectionGroupDTO)[] = []
    ): IConnectionDetailsDTO | IConnectionGroupDTO | undefined {
        const candidateIds = [link.meetingConnectionId, link.connectionGroupId]
            .filter((id): id is number => id != null);

        for (const id of candidateIds) {
            const match = this.findEntityById(id, connections, connectionGroups, inheritanceConnections);
            if (match) {
                return match;
            }
        }
        return undefined;
    }

    private findEntityById(
        id: number | null | undefined,
        connections: IConnectionDetailsDTO[],
        connectionGroups: IConnectionGroupDTO[],
        inheritanceConnections: (IConnectionDetailsDTO | IConnectionGroupDTO)[]
    ): IConnectionDetailsDTO | IConnectionGroupDTO | undefined {
        if (id == null) {
            return undefined;
        }
        return connections.find(item => item.meetingConnectionId === id)
            ?? connectionGroups.find(item => item.meetingConnectionId === id)
            ?? inheritanceConnections.find(item => item.meetingConnectionId === id);
    }

    private resolveInheritedConnectionId(
        role: MeetingLinkRole,
        ids: MeetingLinkInheritanceIds
    ): number | null {
        const candidates = this.inheritanceCandidates(role, ids);
        return candidates.find(id => id != null && id !== 0) ?? null;
    }

    private inheritanceCandidates(
        role: MeetingLinkRole,
        ids: MeetingLinkInheritanceIds
    ): (number | null | undefined)[] {
        if (role === MeetingLinkRole.Presenter) {
            return [
                ids.meetingPresenterRoleConnectionId,
                ids.roomPresenterRoleConnectionId,
                ids.meetingDefaultConnectionId,
                ids.roomDefaultConnectionId,
            ];
        }
        if (role === MeetingLinkRole.Host) {
            return [
                ids.meetingHostRoleConnectionId,
                ids.roomHostRoleConnectionId,
                ids.meetingDefaultConnectionId,
                ids.roomDefaultConnectionId,
            ];
        }
        return [
            ids.meetingSupportRoleConnectionId,
            ids.meetingDefaultConnectionId,
            ids.roomDefaultConnectionId,
        ];
    }
}

export { MeetingLinksViewModel };
